/* eslint-disable import/order */
import React, { useCallback, useEffect } from 'react';

import { Form as AntdForm } from 'antd';
import { FormInstance, FormProps } from 'antd/lib/form';

import { isEmpty } from 'lodash';
import styled from 'styled-components';

import theme from '~/constants/theme';
import { getObjectFieldPaths } from '~/utils/getObjectFieldPaths';
import FormItem from './FormItem';
import ShouldUpdate from './ShouldUpdate';
import Visible from './Visible';
import { useFormItemContext } from './FormItemContext';

const StyledForm = styled(AntdForm)`
  .ant-form-item {
    margin-bottom: ${theme.formVerticalSpacing}px;
  }
`;

const Form = (props: FormProps): any => {
  const { form, onValuesChange, ...rest } = props;
  const formItemContext = useFormItemContext();

  const resetErrors = (formInstance: FormInstance, changedValues: any) => {
    const objectFieldPaths = getObjectFieldPaths(changedValues);
    const fieldsWithErrors = formInstance
      .getFieldsError()
      .filter((item) => !isEmpty(item.errors))
      .filter((item) => objectFieldPaths.find((objectFieldPath) => objectFieldPath.includes(item.name.join('.'))));
    formInstance.setFields(fieldsWithErrors.map((item) => ({ name: item.name, errors: [] })));
  };

  const resetOptionalFieldsErrors = useCallback(
    (optionalFields: Record<string, string>) => {
      if (form) {
        const optionalFieldsPaths = Object.keys(optionalFields);
        const fieldsWithErrors = form
          .getFieldsError()
          .filter((item) => !isEmpty(item.errors))
          .filter((item) => optionalFieldsPaths.includes(item.name.join('.')) && item.errors.includes('Required'));
        form.setFields(fieldsWithErrors.map((item) => ({ name: item.name, errors: [] })));
      }
    },
    [form],
  );

  const handleValuesChange = (changedValues: any, values: any) => {
    if (typeof onValuesChange === 'function') {
      onValuesChange(changedValues, values);
    }
    if (form) {
      resetErrors(form, changedValues);
    }
  };

  useEffect(() => {
    if (formItemContext?.optionalFields) {
      resetOptionalFieldsErrors(formItemContext.optionalFields);
    }
  }, [formItemContext?.optionalFields, resetOptionalFieldsErrors]);

  return (
    <StyledForm
      form={form}
      validateTrigger="onBlur"
      onValuesChange={handleValuesChange}
      scrollToFirstError={scrollToFirstError}
      {...rest}
    />
  );
};

export const scrollToFirstError = { behavior: 'smooth', block: 'center' } as const;

const { List, useForm, Provider } = AntdForm;

Form.Item = FormItem;
Form.List = List;
Form.useForm = useForm;
Form.Provider = Provider;
Form.ShouldUpdate = ShouldUpdate;
Form.Visible = Visible;

export default Form;
